import type { User } from "@/shared/types/auth.types";
import type { TenantConfig } from "@/config/schemas/tenantConfig.schema";
import { FeatureToggleSource } from "./features/feature.types";
import type {
  FeatureFlag,
  GlobalFeatureFlags,
  ExperimentConfig,
  FeatureToggleResult,
} from "./features/feature.types";
import { FeatureFlagEvaluator } from "./features/featureFlagEvaluator";
import { UserBucketService } from "./features/userBucket.service";

// Main feature toggle service - Orchestrates feature resolution
export class FeatureToggleService {
  private static instance: FeatureToggleService;
  private globalFlags: GlobalFeatureFlags = { emergency: {}, defaults: {} };
  private experiments: Record<string, ExperimentConfig> = {};
  private flagEvaluator = FeatureFlagEvaluator.getInstance();
  private userBucketService = UserBucketService.getInstance();

  static getInstance(): FeatureToggleService {
    if (!FeatureToggleService.instance) {
      FeatureToggleService.instance = new FeatureToggleService();
    }

    return FeatureToggleService.instance;
  }

  // Set global emergency toggles (highest priority)
  setEmergencyToggle(featureKey: string, enabled: boolean): void {
    this.globalFlags.emergency[featureKey] = enabled;
  }

  // Set global default flags
  setGlobalDefault(featureKey: string, flag: FeatureFlag): void {
    this.globalFlags.defaults[featureKey] = flag;
  }

  // Set experiment configuration
  setExperiment(experiment: ExperimentConfig): void {
    this.experiments[experiment.key] = experiment;
  }

  // Main feature toggle resolution with full hierarchy
  isFeatureEnabled(
    featureKey: string,
    user: User,
    tenant: TenantConfig,
    userOverrides?: Record<string, boolean>,
  ): FeatureToggleResult {
    // 1. Emergency toggles (highest priority)
    const emergencyResult = this.checkEmergencyToggle(featureKey);
    if (emergencyResult) return emergencyResult;

    // 2. Global flags with rollout logic
    const globalResult = this.checkGlobalFlags(featureKey, user);
    if (globalResult && !globalResult.enabled) return globalResult;

    // 3. Tenant feature flags
    const tenantResult = this.checkTenantFlags(featureKey, tenant);
    if (!tenantResult.enabled) return tenantResult;

    // 4. User-specific overrides (for A/B testing)
    const userOverrideResult = this.checkUserOverrides(
      featureKey,
      user,
      userOverrides,
    );
    if (userOverrideResult) return userOverrideResult;

    // 5. Experiments and rollouts
    const experimentResult = this.checkExperiments(featureKey, user);
    if (experimentResult) return experimentResult;

    // 6. Default to enabled
    return {
      enabled: true,
      reason: "Default enabled",
      source: FeatureToggleSource.Tenant,
    };
  }

  // Initialize with mock data for development
    initializeMockData(): void {
      // Set up some global defaults
      this.setGlobalDefault("beta_features", {
        key: "beta_features",
        enabled: true,
        rolloutPercentage: 50,
        targetRoles: ["admin", "dispatcher"],
        reason: "Beta feature rollout",
      });
  
      this.setGlobalDefault("alpha", {
        key: "alpha",
        enabled: true,
        rolloutPercentage: 100,
        targetRoles: ["admin", "dispatcher"],
        reason: "Alpha feature rollout",
      });

    // Set up experiments
    this.setExperiment({
      key: "new_dashboard_layout",
      enabled: true,
      rolloutPercentage: 25,
      targetRoles: ["admin"],
      variants: {
        control: 50,
        variant_a: 30,
        variant_b: 20,
      },
    });

    this.setExperiment({
      key: "enhanced_trip_form",
      enabled: true,
      rolloutPercentage: 75,
      targetRoles: ["dispatcher", "admin"],
    });
  }

  private checkEmergencyToggle(featureKey: string): FeatureToggleResult | null {
    if (this.globalFlags.emergency[featureKey] !== undefined) {
      const enabled = this.globalFlags.emergency[featureKey];
      return {
        enabled,
        reason: enabled
          ? "Enabled by emergency toggle"
          : "Disabled by emergency toggle",
        source: FeatureToggleSource.Emergency,
      };
    }

    return null;
  }

  private checkGlobalFlags(
    featureKey: string,
    user: User,
  ): FeatureToggleResult | null {
    const globalFlag = this.globalFlags.defaults[featureKey];
    if (!globalFlag) return null;

    return this.flagEvaluator.evaluateFlag(globalFlag, user);
  }

  private checkTenantFlags(
    featureKey: string,
    tenant: TenantConfig,
  ): FeatureToggleResult {
    const tenantEnabled =
      tenant.features?.[featureKey as keyof typeof tenant.features];

    if (tenantEnabled === false) {
      return {
        enabled: false,
        reason: `Feature '${featureKey}' disabled for tenant '${tenant.name}'`,
        source: FeatureToggleSource.Tenant,
      };
    }

    return { enabled: true, source: FeatureToggleSource.Tenant };
  }

  private checkUserOverrides(
    _featureKey: string,
    user: User,
    userOverrides?: Record<string, boolean>,
  ): FeatureToggleResult | null {
    if (userOverrides?.[user.id] !== undefined) {
      const enabled = userOverrides[user.id];
      return {
        enabled,
        reason: enabled
          ? "Enabled by user override"
          : "Disabled by user override",
        source: FeatureToggleSource.User,
      };
    }

    return null;
  }

  private checkExperiments(
    featureKey: string,
    user: User,
  ): FeatureToggleResult | null {
    const experiment = this.experiments[featureKey];
    if (!experiment?.enabled) return null;

    // Check role targeting
    if (
      experiment.targetRoles?.length &&
      !experiment.targetRoles.some((role) => user.roles?.includes(role))
    ) {
      return null;
    }

    // Check rollout percentage
    const inRollout = this.userBucketService.isUserInRollout(
      user.id,
      experiment.key,
      experiment.rolloutPercentage,
    );
    if (!inRollout) return null;

    // Assign variant if configured
    const result: FeatureToggleResult = {
      enabled: true,
      reason: `Included in experiment '${experiment.key}'`,
      source: FeatureToggleSource.Experiment,
    };

    if (experiment.variants) {
      const variant = this.userBucketService.assignVariant(
        user.id,
        experiment.key,
        experiment.variants,
      );
      if (variant) {
        result.variant = variant;
      }
    }

    return result;
  }
}
